import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Check } from "lucide-react";

// Reusable post-purchase password set card (Light Executive System).
export function SetPasswordCard({ email, sessionId, onDone }) {
  const { setPassword, user } = useAuth();
  const [password, setPwd] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  if (user || done) {
    return (
      <div className="exec-card p-5" data-testid="set-password-done">
        <div className="flex items-center gap-3">
          <span className="w-8 h-8 rounded-full border border-[color:var(--copper)] flex items-center justify-center">
            <Check size={16} className="text-[color:var(--copper-deep)]" />
          </span>
          <div className="flex-1">
            <h3 className="text-[12px] uppercase tracking-[0.18em] font-normal text-[color:var(--ink)]">Account ready</h3>
            <p className="exec-serif text-[15.5px] text-[color:var(--lead-ink)] mt-1">You are signed in to the Hub.</p>
          </div>
          <button className="exec-btn exec-btn-secondary" data-testid="go-to-hub-button" onClick={() => navigate("/hub")}>
            Go to the Hub
          </button>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      await setPassword(email, password, sessionId);
      setDone(true);
      if (onDone) onDone();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="exec-card p-5" data-testid="set-password-card">
      <h3 className="text-[12px] uppercase tracking-[0.18em] font-normal text-[color:var(--ink)]">Create your Hub password</h3>
      <p className="exec-serif text-[15.5px] text-[color:var(--lead-ink)] mt-2">
        Set a password for <span className="text-[color:var(--ink)]">{email}</span> to access your engagement and deliverables.
      </p>
      <form onSubmit={submit} className="mt-4 flex flex-col sm:flex-row gap-4 sm:items-end">
        <input
          data-testid="set-password-input"
          type="password"
          className="exec-underline flex-1"
          placeholder="At least 8 characters"
          value={password}
          onChange={(e) => setPwd(e.target.value)}
          autoComplete="new-password"
        />
        <button data-testid="set-password-submit" type="submit" disabled={busy} className="exec-btn exec-btn-primary disabled:opacity-60 whitespace-nowrap">
          {busy ? "\u2026" : "Set password"}
        </button>
      </form>
      {error && (
        <p data-testid="set-password-error" className="exec-serif text-[16px] text-[color:var(--ink)] border-l-2 border-[color:var(--copper)] pl-3 mt-3">
          {error}
        </p>
      )}
    </div>
  );
}
